import React, { useMemo, useState } from 'react';
import { CohortPatientOverview } from '../../types/domain';
import {
  CohortSortField,
  cohortSummary,
  filterCohort,
  flagPatients,
  sortCohort
} from '../../lib/cohort';
import { formatNumber } from '../../lib/monthlyReport';

interface CohortMonitorProps {
  rows: CohortPatientOverview[];
  loading: boolean;
  windowDays: number;
  onWindowChange: (days: number) => void;
  onSelectPatient: (patientId: string) => void;
  selectedPatientId?: string | null;
}

/** The windows a paediatric clinic actually asks for. */
const WINDOW_OPTIONS = [7, 14, 30, 90];

const COLUMNS: { field: CohortSortField; label: string; hint: string }[] = [
  { field: 'name', label: 'Pasien', hint: 'Urut nama' },
  { field: 'hypo', label: 'Hipo', hint: 'Terbanyak dulu' },
  { field: 'inTarget', label: 'Di target', hint: 'Terendah dulu' },
  { field: 'compliance', label: 'Catat', hint: 'Terendah dulu' },
  { field: 'insulin', label: 'Insulin/hari', hint: 'Tertinggi dulu' },
  { field: 'hba1c', label: 'HbA1c', hint: 'Tertinggi dulu' },
  { field: 'lastEntry', label: 'Terakhir', hint: 'Terlama dulu' }
];

/**
 * The doctor's cohort at a glance (FR-14).
 *
 * Two halves, in the order a clinician reads: the children who need attention,
 * then everybody. The alarm list is not a filter on the table — a doctor who
 * has to sort a table to discover a hypoglycemia has already scrolled past it.
 *
 * Every number shown arrives banded by `doctor_patient_overview()` against
 * that patient's own FR-06 thresholds, so "hipo" means the same thing for a
 * child with a doctor-set override as for one on the clinic default.
 */
export const CohortMonitor: React.FC<CohortMonitorProps> = ({
  rows,
  loading,
  windowDays,
  onWindowChange,
  onSelectPatient,
  selectedPatientId
}) => {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<CohortSortField>('hypo');

  const flagged = useMemo(() => flagPatients(rows), [rows]);
  const summary = useMemo(() => cohortSummary(rows), [rows]);
  const visible = useMemo(
    () => sortCohort(filterCohort(rows, query), sort),
    [rows, query, sort]
  );

  if (loading) {
    return <div className="cohort-empty">Memuat data pemantauan...</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="cohort-empty">
        Belum ada pasien untuk ditampilkan. Dokter hanya melihat pasien di tempat
        kontrol yang ditugaskan kepadanya.
      </div>
    );
  }

  return (
    <div className="cohort">
      <div className="cohort-toolbar">
        <div className="cohort-window">
          <label htmlFor="cohort-window">Rentang</label>
          <select
            id="cohort-window"
            value={windowDays}
            onChange={event => onWindowChange(Number(event.target.value))}
          >
            {WINDOW_OPTIONS.map(days => (
              <option key={days} value={days}>
                {days} hari terakhir
              </option>
            ))}
          </select>
        </div>
        <input
          type="search"
          className="cohort-search"
          placeholder="Cari nama atau tempat kontrol"
          aria-label="Cari pasien"
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      </div>

      <div className="report-stats">
        <CohortStat label="Pasien" value={String(summary.patients)} note="dalam pengawasan" />
        <CohortStat
          label="Ada hipoglikemia"
          value={String(summary.withHypo)}
          note={`dalam ${windowDays} hari terakhir`}
        />
        <CohortStat
          label="Berhenti mencatat"
          value={String(summary.inactive)}
          note="5 hari atau lebih"
        />
        <CohortStat
          label="Mencatat hari ini"
          value={String(summary.recordingToday)}
          note={`dari ${summary.patients} pasien`}
        />
        <CohortStat
          label="Rata-rata kepatuhan"
          value={`${summary.averageCompliance}%`}
          note="hari tercatat per pasien"
        />
      </div>

      <section className="cohort-section" aria-labelledby="cohort-alerts-title">
        <h3 className="report-section-title" id="cohort-alerts-title">
          Perlu perhatian
        </h3>
        {flagged.length === 0 ? (
          <p className="cohort-empty">
            Tidak ada tanda bahaya pada {windowDays} hari terakhir. Semua pasien mencatat
            dan tidak ada hipoglikemia tercatat.
          </p>
        ) : (
          <ul className="cohort-alerts">
            {flagged.map(({ patient, alerts, severity }) => (
              <li key={patient.patientId} className={`cohort-alert ${severity}`}>
                <button
                  type="button"
                  className="cohort-alert-button"
                  onClick={() => onSelectPatient(patient.patientId)}
                >
                  <span className="cohort-alert-name">
                    {patient.fullName ?? 'Tanpa nama'}
                    <span className="cohort-alert-place">{patient.controlPlace}</span>
                  </span>
                  <span className="cohort-alert-flags">
                    {alerts.map(alert => (
                      <span
                        key={alert.kind}
                        className={`cohort-flag ${alert.kind} ${alert.severity}`}
                      >
                        <strong>{alert.label}</strong>
                        <em>{alert.detail}</em>
                      </span>
                    ))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="cohort-section" aria-labelledby="cohort-table-title">
        <h3 className="report-section-title" id="cohort-table-title">
          Semua pasien
        </h3>
        <div className="cohort-table-wrap">
          <table className="cohort-table">
            <caption className="sr-only">
              Ringkasan {windowDays} hari terakhir untuk setiap pasien. Klik judul kolom
              untuk mengurutkan.
            </caption>
            <thead>
              <tr>
                {COLUMNS.map(column => (
                  <th key={column.field} scope="col">
                    <button
                      type="button"
                      className={sort === column.field ? 'active' : undefined}
                      onClick={() => setSort(column.field)}
                      title={column.hint}
                      aria-pressed={sort === column.field}
                    >
                      {column.label}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(row => (
                <tr
                  key={row.patientId}
                  className={row.patientId === selectedPatientId ? 'selected' : undefined}
                >
                  <th scope="row">
                    <button type="button" onClick={() => onSelectPatient(row.patientId)}>
                      {row.fullName ?? 'Tanpa nama'}
                    </button>
                  </th>
                  <td className={row.hypoCount > 0 ? 'danger' : undefined}>
                    {row.hypoCount}
                  </td>
                  <td>{row.inTargetPercent === null ? '–' : `${row.inTargetPercent}%`}</td>
                  <td>{row.compliancePercent}%</td>
                  <td>{formatNumber(row.averageDailyUnits)} u</td>
                  <td>{row.latestHba1c === null ? '–' : formatNumber(row.latestHba1c)}</td>
                  <td>
                    {row.lastEntryDate === null
                      ? 'belum ada'
                      : row.daysSinceEntry === 0
                        ? 'hari ini'
                        : `${row.daysSinceEntry} hari`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {visible.length === 0 ? (
          <p className="cohort-empty">Tidak ada pasien yang cocok dengan pencarian.</p>
        ) : null}
      </section>
    </div>
  );
};

const CohortStat: React.FC<{ label: string; value: string; note: string }> = ({
  label,
  value,
  note
}) => (
  <div className="report-stat">
    <span className="report-stat-label">{label}</span>
    <span className="report-stat-value">{value}</span>
    <span className="report-stat-note">{note}</span>
  </div>
);

export default CohortMonitor;
